import { useEffect, useState, useRef } from 'react';
import GoogleAd from '../../GoogleAds/index.jsx';
import CardArticle from './components/card-article.component.jsx';
import CardArticleLoading from './components/card-article-loading.component.jsx';
import EmptyArticleComponent from '../../EmptyArticleComponent/index.jsx';
import NewsHubComponent from './components/news-hub.component';
import { useAtom } from 'jotai';
import { profileAtom } from '../../../components/Settings/atom';
import { TAB_LIST_DISCOVERY } from '../../../constants/article.constant';
import MicroLearningCard from '../../MicroLearning/MicroLearningCard';
import TutorialCard from '../../MicroLearning/TutorialCard';
import TutorialModal from '../../MicroLearning/TutorialModal';
import { useSimpleFeedSession } from '../../../hooks/useSimpleFeedSession';
import { useSimpleInjection } from '../../../hooks/useSimpleInjection';
import { useLearningTutorial } from '../../../hooks/useLearningTutorial';

const MiddleContentComponent = ({ selected, listArticle, loading }) => {
  const [profile] = useAtom(profileAtom);
  const [injectedModuleInsertIndex, setInjectedModuleInsertIndex] = useState(null);
  const previousArticleCountRef = useRef(0);
  const hasCheckedGuaranteedRef = useRef(false);

  // Tutorial state
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const {
    hasCompletedTutorial,
    isLoading: tutorialLoading,
    markTutorialComplete
  } = useLearningTutorial();

  // Determine if this is initial loading or load-more loading
  const isInitialLoading = loading && (!listArticle || listArticle.length === 0);
  const isLoadMoreLoading = loading && listArticle && listArticle.length > 0;

  // Simple session tracking - just count articles and time
  const { timeOnPlatform, recordArticlesLoaded } = useSimpleFeedSession();

  // Simple injection - check when we call it
  const {
    injectedModule,
    guaranteedModule,
    checkAndInject,
    checkGuaranteedPosition,
    recordModuleStart,
    recordModuleComplete,
    recordModuleAbandon,
    recordGuaranteedModuleStart,
    recordGuaranteedModuleComplete,
    recordGuaranteedModuleAbandon,
    clearModule,
    clearGuaranteedModule,
    resetForNewFeed
  } = useSimpleInjection();

  // Check for guaranteed position 4 module on initial load
  // Only if user has completed the learning tutorial
  useEffect(() => {
    // Wait for tutorial status to load
    if (tutorialLoading) return;

    // Only check once when we have at least 4 articles
    if (!hasCheckedGuaranteedRef.current && listArticle && listArticle.length >= 4 && !loading) {
      hasCheckedGuaranteedRef.current = true;

      // Only show guaranteed learning module if tutorial is completed
      if (hasCompletedTutorial) {
        console.log('[Feed] 🎯 Tutorial complete - checking for guaranteed learning module...');
        checkGuaranteedPosition();
      } else {
        console.log('[Feed] 📚 Tutorial not complete - will show tutorial card at position 4');
      }
    }
  }, [
    listArticle?.length,
    loading,
    checkGuaranteedPosition,
    hasCompletedTutorial,
    tutorialLoading
  ]);

  // Reset injection state when tab changes
  useEffect(() => {
    hasCheckedGuaranteedRef.current = false;
    previousArticleCountRef.current = 0;
    setInjectedModuleInsertIndex(null);
    resetForNewFeed();
  }, [selected.type, resetForNewFeed]);

  // When articles load or change, record the count and check for injection
  useEffect(() => {
    if (!listArticle || listArticle.length === 0 || loading) {
      return;
    }

    const currentCount = listArticle.length;
    const previousCount = previousArticleCountRef.current;

    // Only process if article count actually increased (new articles loaded)
    if (currentCount > previousCount) {
      const newArticles = currentCount - previousCount;
      console.log('[Feed] 📥 Articles loaded:', newArticles, 'new articles. Total:', currentCount);

      // Record how many articles were loaded
      recordArticlesLoaded(newArticles);

      // On initial load (previousCount was 0), let the guaranteed check handle position 4
      // On subsequent loads (load more, pagination), check for regular injection
      const isInitialLoad = previousCount === 0;

      if (!isInitialLoad) {
        // Check if we should inject another module as user scrolls
        // This can fire even if guaranteed module is showing - they'll be at different positions
        checkAndInject(currentCount, timeOnPlatform);
      }

      previousArticleCountRef.current = currentCount;
    }
  }, [listArticle?.length, loading, recordArticlesLoaded, checkAndInject, timeOnPlatform]);

  // When a module is injected, determine where to insert it (at the end)
  useEffect(() => {
    if (injectedModule && injectedModuleInsertIndex === null) {
      const articleCount = listArticle?.length || 0;

      // Simple: just append at the end
      // This is the most reliable - always visible, doesn't disrupt reading flow
      const insertIndex = articleCount;

      setInjectedModuleInsertIndex(insertIndex);
      console.log('[Feed] ✅ Module will be appended at end (index:', insertIndex, ')');
    }
  }, [injectedModule, injectedModuleInsertIndex, listArticle]);

  // Clear injection when user navigates away or list changes significantly
  useEffect(() => {
    setInjectedModuleInsertIndex(null);
  }, [selected.type]);

  // Handle tutorial start
  const handleTutorialStart = () => {
    setIsTutorialModalOpen(true);
  };

  // Handle tutorial completion
  const handleTutorialComplete = async () => {
    await markTutorialComplete();
    setIsTutorialModalOpen(false);
    // Trigger guaranteed module check now that tutorial is done
    if (listArticle && listArticle.length >= 4) {
      console.log('[Feed] 🎯 Tutorial just completed - checking for guaranteed module...');
      checkGuaranteedPosition();
    }
  };

  const renderContentWithAds = () => {
    const contentWithAds = [];
    const articleCount = listArticle?.length || 0;

    // Determine what to show at position 4
    const showTutorialCard = !tutorialLoading && !hasCompletedTutorial;
    const showGuaranteedModule = hasCompletedTutorial && guaranteedModule;

    console.log('[Feed] 🎨 Rendering feed:', {
      hasInjectedModule: !!injectedModule,
      hasGuaranteedModule: !!guaranteedModule,
      showTutorialCard,
      showGuaranteedModule,
      injectedModuleInsertIndex,
      articleCount
    });

    listArticle?.forEach((item, index) => {
      // POSITION 4: Either Tutorial Card OR Guaranteed Learning Module
      // Insert BEFORE adding the 4th article (index 3)
      if (index === 3) {
        if (showTutorialCard) {
          // User hasn't completed tutorial - show tutorial card
          console.log('[Feed] 📚 Rendering TUTORIAL card at position 4');
          contentWithAds.push(<TutorialCard key="tutorial-card" onStart={handleTutorialStart} />);
        } else if (showGuaranteedModule) {
          // Tutorial complete, show guaranteed learning module
          console.log('[Feed] 🎯 Rendering GUARANTEED module at position 4');
          contentWithAds.push(
            <MicroLearningCard
              key={`guaranteed-learning-${guaranteedModule.id}`}
              module={guaranteedModule}
              userProgress={guaranteedModule.userProgress}
              onStart={recordGuaranteedModuleStart}
              onComplete={recordGuaranteedModuleComplete}
              onAbandon={recordGuaranteedModuleAbandon}
              onClose={() => {
                console.log('[Feed] 🗑️ Guaranteed module dismissed');
                clearGuaranteedModule();
              }}
            />
          );
        }
      }

      // Add article
      contentWithAds.push(
        <CardArticle
          key={`article-${index}`}
          index={index}
          item={{
            ...item,
            nameCreator: item?.creator?.firstName ?? '',
            imgUrl: item?.creator?.imgUrl
          }}
          isHiddenFeaturedArticles={selected.type === 'following' || selected.type === 'myReading'}
          isProfile={profile.id === item?.creator?.id}
        />
      );

      // Add advertisement every 4th article (but skip position 4 if we have tutorial/guaranteed module there)
      const hasPosition4Content = showTutorialCard || showGuaranteedModule;
      if ((index + 1) % 4 === 0 && !(hasPosition4Content && index === 3)) {
        contentWithAds.push(
          <GoogleAd
            articleId={item?.id}
            key={`ad-${index}`}
            uniqueKey={`ad-${index}`}
            slot="1625116786"
            className="my-custom-ad !z-10"
          />
        );
      }

      // Inject intelligent learning module at calculated position (regular injection)
      // Skip if this is position 4 and we already have tutorial/guaranteed content
      if (injectedModule && injectedModuleInsertIndex === index) {
        if (!(hasPosition4Content && index === 3)) {
          console.log('[Feed] 📌 Rendering regular module at index:', index);
          contentWithAds.push(
            <MicroLearningCard
              key={`intelligent-learning-${injectedModule.id}`}
              module={injectedModule}
              userProgress={injectedModule.userProgress}
              onStart={recordModuleStart}
              onComplete={recordModuleComplete}
              onAbandon={recordModuleAbandon}
              onClose={() => {
                console.log('[Feed] 🗑️ Module dismissed');
                clearModule();
                setInjectedModuleInsertIndex(null);
              }}
            />
          );
        }
      }
    });

    // Append regular module at end if index is at/beyond article count
    if (
      injectedModule &&
      injectedModuleInsertIndex !== null &&
      injectedModuleInsertIndex >= articleCount
    ) {
      console.log('[Feed] 📌 Appending module at end');
      contentWithAds.push(
        <MicroLearningCard
          key={`intelligent-learning-${injectedModule.id}`}
          module={injectedModule}
          userProgress={injectedModule.userProgress}
          onStart={recordModuleStart}
          onComplete={recordModuleComplete}
          onAbandon={recordModuleAbandon}
          onClose={() => {
            console.log('[Feed] 🗑️ Module dismissed');
            clearModule();
            setInjectedModuleInsertIndex(null);
          }}
        />
      );
    }

    console.log('[Feed] Total items rendered:', contentWithAds.length);
    return contentWithAds;
  };

  // Handle News Hub tab specifically
  if (selected.type === TAB_LIST_DISCOVERY.NEWS_HUB) {
    return (
      <div className="pb-[30px] px-1 md:px-4">
        <div className="flex flex-col gap-6 2xl:gap-10 p-1">
          <NewsHubComponent loading={loading} setLoading={() => {}} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="pb-[30px] w-full">
        <div className="flex flex-col divide-y divide-gray-200">
          {renderContentWithAds()}

          {/* Initial loading skeleton cards */}
          {isInitialLoading &&
            [...Array(3)].map((_, i) => (
              <CardArticleLoading
                key={i}
                index={i}
                isHiddenFeaturedArticles={
                  selected.type === 'following' || selected.type === 'myReading'
                }
              />
            ))}

          {/* Load more loading spinner */}
          {isLoadMoreLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-growth-green-500"></div>
                <span className="text-sm text-gray-600 font-medium">Loading more articles...</span>
              </div>
            </div>
          )}

          {listArticle?.length === 0 && !loading && (
            <EmptyArticleComponent categoryType={selected.type} />
          )}
        </div>
      </div>

      {/* Learning Tutorial Modal */}
      <TutorialModal
        isOpen={isTutorialModalOpen}
        onClose={() => setIsTutorialModalOpen(false)}
        onComplete={handleTutorialComplete}
      />
    </>
  );
};

export default MiddleContentComponent;
